import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useEffect, useState } from "react";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import TextField from "@mui/material/TextField";
import Input from "@mui/material/Input";
import { useFileRepository } from "../../FileRepositoryContext";
import { FolderList } from "../../modal";
import { ArrowDropDown } from "@mui/icons-material";
import { InputAdornment, IconButton, Typography, List, ListItem, ListItemText } from "@mui/material";

interface DialogBoxFolderProps {
    parentFolderId?: any;
    openDialogBox: boolean;
    dialogTitle?: string;
    dialogAction?: string;
    dialogBody?: FolderList | null;
    dialogActionCancel?: string;
    handleFunction?: (folderName: string, folderData?: FolderList | null, file?: File[]) => void;
    handleCancel?: () => void;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const DialogBoxForm: React.FC<DialogBoxFolderProps> = (props) => {
    const [open, setOpen] = useState(false);
    const { folderList } = useFileRepository();
    const [dialogTitle, setDialogTitle] = useState<string>("");
    const [dialogAction, setDialogAction] = useState<string>("");
    const [dialogActionCancel, setDialogActionCancel] = useState<string>("");
    const [folderName, setFolderName] = useState<string>("");
    const [selectedFile, setSelectedFile] = useState<File[]>([]);
    const [folderNameError, setFolderNameError] = useState<string>("");
    const [folderData, setFolderData] = useState<FolderList | null>(null)
    const [hasErrorMessage, setHasErrorMessage] = useState<string>("")

    useEffect(() => {
        setOpen(props.openDialogBox);
        setDialogTitle(props.dialogTitle || "");
        setDialogAction(props.dialogAction || "");
        setDialogActionCancel(props.dialogActionCancel || "");
        setFolderData(props.dialogBody || null);
    }, [
        props.openDialogBox,
        props.dialogTitle,
        props.dialogAction,
        props.dialogActionCancel,
        props.parentFolderId,
    ]);

    const handleClose = () => {
        if (props.handleCancel) {
            props.handleCancel();
        }
        setFolderNameError("");
        setFolderName("");
        setSelectedFile([]);
    };

    const handleOk = () => {

        let folderNameExists = props.parentFolderId
            ? folderList
                ?.find((folder) => folder.folderId === props.parentFolderId)
                ?.subFolders.some(
                    (folder: FolderList) =>
                        folderName.toLocaleLowerCase() ===
                        folder.folderName?.toLocaleLowerCase()
                )
            : folderList?.some(
                (folder: FolderList) =>
                    folderName.toLocaleLowerCase() ===
                    folder.folderName?.toLocaleLowerCase()
            );
        if (folderNameExists) {
            setFolderNameError("This folder name already exists.");
            return;
        }

        if (props.handleFunction) {
            props.handleFunction(folderName, folderData, selectedFile);
        }
        handleClose();
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        handleOk();
    };

    const handleInputChange = (event: any) => {
        setFolderName(event.target.value);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {

            const files = Array.from(event.target.files);
            setSelectedFile(files);
        }
    };

    useEffect(() => {
        if (folderData?.folderName && dialogTitle === "Rename Folder") {
            setFolderName(folderData.folderName)
        }
    }, [folderData])
    useEffect(() => {
        if (folderName) {
            if (folderName.length <= 30) {
                setFolderNameError("")
            } else {
                const trimmedFolderName = folderName.slice(0, 30)
                setFolderName(trimmedFolderName)
                setFolderNameError("Folder name cannot exceed 30 characters.");
            }
        }

    }, [folderName])


    const isDisabled = (): boolean => {
        return !!(folderData && folderData?.folderName?.toLowerCase() === folderName?.toLowerCase());
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            TransitionComponent={Transition}
            keepMounted
            PaperProps={{
                component: "form",
                onSubmit: handleFormSubmit,
            }}
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
            <DialogContent>
                {dialogTitle === "Upload Files" ? (
                    <div>
                        <Input
                            type="file"
                            inputProps={{ accept: "*/*", multiple: true }}
                            onChange={handleFileChange}
                            required
                            fullWidth
                            margin="dense"
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton edge="end">
                                        <ArrowDropDown />
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                        {selectedFile.length > 1 && (
                            <Typography variant="body2" margin="dense">
                                Selected files:
                                <List>
                                    {selectedFile.map((file, index) => (
                                        <ListItem key={index}>
                                            <ListItemText primary={file.name} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Typography>
                        )}
                    </div>
                ) : (
                    <TextField
                        autoFocus
                        required
                        margin="dense"
                        label="Name"
                        fullWidth
                        // variant="outlined"
                        variant="filled"
                        value={folderName}
                        onChange={(event) => {
                            if (event.target.value.length > 30) {
                                setFolderNameError("Folder name cannot exceed 30 characters.");
                            } else {
                                handleInputChange(event);
                            }
                        }}
                        error={!!folderNameError}
                        helperText={folderNameError}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    size="small"
                    variant="contained"
                    onClick={handleClose}
                    sx={{
                        backgroundColor: "rgba(0, 102, 255, 0.1)",
                        color: "#0066FF",
                        "&:hover": {
                            bgcolor: "#0066ff2e",
                        },
                    }}
                >
                    {dialogActionCancel}
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    type="submit"
                    sx={{
                        backgroundColor: "#0066FF",
                        "&:hover": {
                            bgcolor: "#0556d0",
                        },
                    }}
                    disabled={isDisabled()}
                >
                    {dialogAction}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DialogBoxForm;
